import React, { useEffect, useState } from 'react';
import { useTheme } from '../../../theme-context';
import Input from '../../../shared/input';
import { Controller, useForm } from 'react-hook-form';
import InputColor, { InputColorProps } from 'react-input-color';
import { tryToUploadFiles } from '../../../store/brokers/partners/knowledge-files/knowledgeFilesSlice';
import { useAppDispatch, useAppSelector } from '../../../app/hooks';
import { tryToCreateCards, tryToEditCard } from '../../../store/brokers/partners/history-chats/historyChatsSlice';
import { ICards } from '../../../interfaces/cards/ICards';
import Button from '../../../shared/button';
import Toast from '../../../shared/toast';
import { IToast } from '../../../interfaces/components/IToast';

const EmployeeUpdatedNews = ({ 
	getCards, 
	cardToEdit,
	onClickGoBack
}: { 
	getCards: () => void 
	cardToEdit?: ICards
	onClickGoBack: () => void
}) => {
    const [showToast, setShowToast] = useState<IToast | null>();
    const cardIsLoading = useAppSelector(
        (state) => state.history.cardIsLoading,
    );
    const dispatch = useAppDispatch();
    const fileInputRef = React.useRef<HTMLInputElement | null>(null);
    const { theme } = useTheme();

    const {
        register,
        handleSubmit,
        watch,
        formState: { errors },
        control,
        setValue,
        reset,
    } = useForm<ICards>();

	useEffect(()=>{
		if (cardToEdit) {
			const {_id, title, subtitle, color, image} = cardToEdit;
			setValue('_id', _id)
			setValue('title', title)
			setValue('subtitle', subtitle)
			setValue('color', color)
			setValue('colorObj.hex', color)
			setValue('image', image)
		} else {
			setValue('_id', '')
			setValue('title', '')
			setValue('subtitle', '')
			setValue('color', '')
			setValue('colorObj.hex', '')
			setValue('image', '')
		}
	},[cardToEdit])

    const onSubmit = async (data: ICards) => {
        try {
			if (cardToEdit) {
				await dispatch(
					tryToEditCard({ ...data, color: data.colorObj.hex }),
				).unwrap();
				onClickGoBack()
			} else {
				await dispatch(
					tryToCreateCards({ ...data, color: data.colorObj.hex }),
				).unwrap();
			}
            
            reset({
                title: '',
                subtitle: '',
                image: '',
                color: '',
                colorObj: { hex: '' },
            });
            setShowToast({
                type: 'success',
                message: 'Card has been added successfully',
            });
            getCards();
        } catch (e) {
            const errMsg = e as string[];
            setShowToast({ type: 'error', message: errMsg?.join() });
            console.error({ e });
        }
    };

    const onUploadClick = () => fileInputRef.current?.click();

    const onFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = event.target.files?.[0];
        if (!selectedFiles) return;
        const formData = new FormData();
        formData.append('files', selectedFiles);
        try {
            const response = await dispatch(
                tryToUploadFiles(formData),
            ).unwrap();
            setValue('image', response[0].url);
        } catch (error) {
            console.error(`Error uploading file ${selectedFiles.name}`, error);
        }
    };
    return (
        <form onSubmit={handleSubmit(onSubmit)} className="flex flex-wrap">
            <div className="w-full md:w-6/12">
                <Input
                    register={register('title')}
                    label="Title"
                    containerStyle="min-w-full px-2"
                    error={errors.title?.message}
                />
            </div>
            <div className="w-full md:w-6/12">
                <Input
                    register={register('subtitle')}
                    label="Description"
                    containerStyle="min-w-full px-2"
                    error={errors.subtitle?.message}
                />
            </div>
            <div className="w-full md:w-6/12 px-2">
                {watch('image') ? (
                    <div className="w-full border p-2 truncate">
                        {' '}
                        {watch('image')}
                    </div>
                ) : (
                    <div>
                        <div
                            onClick={onUploadClick}
                            className="flex flex-row items-center justify-center px-2 py-1 border-2 border-black rounded-xl dark:border-borderColor-darkMode hover:opacity-80 cursor-pointer">
                            <img
                                src={
                                    theme === 'dark'
                                        ? '/assets/documents/upload-docs-white.svg'
                                        : '/assets/documents/upload-docs.svg'
                                }
                                className="object-contain w-[20px]"
                            />
                            <p className="font-semibold dark:text-primary-dark ml-2">
                                Upload new file
                            </p>
                        </div>
                        <input
                            type="file"
                            multiple
                            ref={fileInputRef}
                            style={{ display: 'none' }}
                            onChange={onFileChange}
                            accept="*"
                        />
                    </div>
                )}
            </div>
            <div className="w-full md:w-6/12">
                <Controller
                    control={control}
                    name="colorObj"
                    render={({ field: { onChange } }) => (
                        <InputColor
                            {...({
                                className: '!w-[calc(100%-16px)] !mx-2 !h-10',
                                initialValue: String(watch('color')) || '#5e72e4',
                                onChange,
                                placement: 'right',
                            } as InputColorProps)}
                        />
                    )}
                />
            </div>
            <div className="w-full p-2">
                <Button
                    className="btn-border-rounded !shadow-none min-w-full mr-3 dark:border-white dark:text-white"
                    disabled={cardIsLoading}
                    loading={cardIsLoading}
                    type="submit"
                    label={cardToEdit ? 'Save changes' : 'Create'}
                />
            </div>
            {showToast?.message && (
                <Toast
                    type={showToast?.type}
                    text={showToast?.message}
                    onHandleCloseToast={() => setShowToast(null)}
                />
            )}
        </form>
    );
};

export default EmployeeUpdatedNews;
